import { FC, useRef } from 'react';
import classNames from 'classnames';
import { Controller } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faMicrophone,
} from '@fortawesome/pro-regular-svg-icons';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import type { AudioMemo } from 'models/Memo';
import TitleField from '../fields/TitleField';
import { useTranslations } from 'next-intl';
import { useMemoFormContext } from '../context/FormContext';
import { useMediaUpload } from '../hooks/useMediaUpload';
import { AudioOptionCards } from './audio/AudioOptionCards';
import { RecordingBar } from './audio/RecordingBar';
import { AudioClipCard } from './audio/AudioClipCard';
import { useAudioRecorder } from './audio/useAudioRecorder';

interface Props {
  memo?: AudioMemo | null;
}

// The audio composer: a description, then either the two ways to add
// audio, the recording in progress, or the clips so far.
const AudioForm: FC<Props> = ({ memo }) => {
  const { control, isTitleVisible } = useMemoFormContext();
  const t = useTranslations();
  const { selectedBox } = useActiveSpace();
  const boxId = memo?.boxId ?? selectedBox?.id;

  const { items, addFiles, removeItem } = useMediaUpload({
    name: 'audioIds',
    boxId,
  });
  const recorder = useAudioRecorder({ onRecorded: (file) => addFiles([file]) });
  const inputRef = useRef<HTMLInputElement>(null);
  const pickFiles = () => inputRef.current?.click();

  return (
    <div className={styles.container}>
      {isTitleVisible && <TitleField />}
      <Controller
        control={control}
        name="content"
        render={({ field: { onChange, value, onBlur } }) => (
          <TextareaAutosize
            minRows={1}
            maxRows={5}
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            className={styles.caption}
            placeholder={t('memoComposer.descriptionPlaceholder')}
          />
        )}
      />

      {recorder.isRecording ? (
        <RecordingBar
          elapsed={recorder.elapsed}
          levels={recorder.levels}
          onStop={recorder.stop}
        />
      ) : items.length === 0 ? (
        <AudioOptionCards onRecord={recorder.start} onUpload={pickFiles} />
      ) : (
        <>
          <ul className={styles.list}>
            {items.map((item) => (
              <li key={item.key}>
                <AudioClipCard
                  previewUrl={item.isExisting ? undefined : item.previewUrl}
                  mediaId={item.isExisting ? item.mediaId : undefined}
                  status={item.status}
                  onRemove={() => removeItem(item.key)}
                />
              </li>
            ))}
          </ul>

          {/* Room for more, in a quieter form than the opening cards. */}
          <div className={styles.more}>
            <button
              type="button"
              onClick={recorder.start}
              className={styles.moreBtn}
            >
              <FontAwesomeIcon icon={faMicrophone} aria-hidden="true" />
              <span>{t('memoComposer.audio.record')}</span>
            </button>
            <button
              type="button"
              onClick={pickFiles}
              className={styles.moreBtn}
            >
              <FontAwesomeIcon icon={faArrowUpFromBracket} aria-hidden="true" />
              <span>{t('memoComposer.upload')}</span>
            </button>
          </div>
        </>
      )}

      {recorder.isDenied && (
        <p className={styles.micError}>{t('memoComposer.audio.micDenied')}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        hidden
        onChange={(event) => {
          addFiles(
            Array.from(event.target.files ?? []).filter((file) =>
              file.type.startsWith('audio/'),
            ),
          );
          event.target.value = '';
        }}
      />
    </div>
  );
};

export default AudioForm;

const styles = {
  container: 'space-y-3',
  caption:
    'block w-full bg-transparent resize-none border-0 px-0 py-0 text-base text-ink-primary placeholder-ink-tertiary focus:ring-0',
  list: 'space-y-2',
  more: 'flex gap-2',
  moreBtn: classNames(
    'inline-flex items-center gap-2 rounded-full border border-stroke px-3 py-1.5 text-xs font-medium text-ink-secondary',
    'hover:bg-ink-secondary/10 hover:text-ink-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
  ),
  micError: 'text-sm text-mf-red-600',
};
